/**
 * Partner Self-Service Portal — API + auth helpers.
 * Partner-portal users have a distinct JWT (localStorage key `hw_partner_access_token`)
 * so their session never collides with staff or volunteer sessions.
 */
import axios from "axios";

const TOKEN_KEY = "hw_partner_access_token";
const PROFILE_KEY = "hw_partner_profile";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const partnerApi = axios.create({ baseURL: API });

partnerApi.interceptors.request.use((cfg) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

partnerApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(PROFILE_KEY);
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.startsWith("/partner/login") &&
        !window.location.pathname.startsWith("/partner/accept-invite")
      ) {
        window.location.href = "/partner/login?expired=1";
      }
    }
    return Promise.reject(err);
  },
);

export const storePartnerSession = (token, profile) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
};

export const getStoredPartnerProfile = () => {
  try {
    return JSON.parse(localStorage.getItem(PROFILE_KEY) || "null");
  } catch {
    return null;
  }
};

export const clearPartnerSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(PROFILE_KEY);
};

export const isPartnerLoggedIn = () => !!localStorage.getItem(TOKEN_KEY);

export const formatPartnerError = (err) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((d) => d.msg || JSON.stringify(d)).join(", ");
  if (detail && typeof detail === "object")
    return detail.message || JSON.stringify(detail);
  return err?.message || "Something went wrong";
};

export const PARTNER_ROLE_LABELS = {
  owner: "Owner",
  admin: "Admin",
  developer: "Developer",
};

export const canPartnerAdmin = (profile) =>
  ["owner", "admin"].includes(profile?.partner_user?.partner_role);

export const isPartnerOwner = (profile) =>
  profile?.partner_user?.partner_role === "owner";
